import React, {memo, useState} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import type {SxProps, Theme} from '@mui/material/styles';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import SectionHeader from '@/components/Common/SectionHeader';
import {borderWidth, colors, fontSize, fontWeight} from '@/styles/tokens';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {fadeInUp} from '@/animation/scroll-animation';
import parse from 'html-react-parser';

import {AnimatedComponent} from '../AnimatedComponent';

// ─── Module-level data ────────────────────────────────────────────────────────
import {topCardsData, checklistData} from '@/util/contents/blockchain-data';
import DisplayImage from '../display-image';

// ─── Module-level sx constants ────────────────────────────────────────────────
const topCardSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    border: `${borderWidth.thin} solid #EAEAEA`,
    borderRadius: 3,
    py: {xs: 4, md: 4},
    px: {xs: 2, md: 2.5},
    height: '100%',
    bgcolor: colors.background.default,
    boxShadow: '0 10px 40px rgba(0,0,0,0.04)'
} as const;

const topCardImageWrapperSx = {
    position: 'relative',
    width: '100%',
    height: 170,
    mb: 4,
    transition: 'transform 0.3s ease',
    '&:hover': {
        transform: 'scale(1.08)'
    }
} as const;

const checklistContainerSx = {
    mt: 10,
    borderTop: `${borderWidth.thin} solid #EBEBEB`
} as const;

const checklistItemSx = {
    py: {xs: 4, md: 5},
    borderBottom: `${borderWidth.thin} solid #EBEBEB`,
    display: 'flex',
    flexDirection: {xs: 'column', md: 'row'},
    alignItems: 'flex-start',
    gap: {xs: 0, md: 4}
} as const;

const checkIconWrapperSx = {
    mt: 0.5,
    flexShrink: 0
} as const;

const checklistTitleSx = {
    fontWeight: fontWeight.bold,
    fontSize: {xs: fontSize[18], md: fontSize[20]},
    color: colors.text.primary,
    pr: {xs: 0, md: 4},
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    cursor: {xs: 'pointer', md: 'default'},
    '& a': {
        color: colors.text.primary,
        textDecoration: 'underline',
        '&:hover': {
            opacity: 0.8
        }
    }
} as const;

const checklistContentBoxSx = {
    width: {xs: '100%', md: '65%'},
    display: 'flex',
    flexDirection: 'column',
    gap: 2
} as const;

const footerTextSx = {
    fontWeight: fontWeight.bold,
    fontSize: fontSize[16],
    color: colors.text.primary,
    mt: 1,
    '& a': {
        color: colors.text.primary,
        textDecoration: 'underline',
        '&:hover': {
            opacity: 0.8
        }
    }
} as const;

const listItemTextSx = {
    color: colors.text.primary,
    fontSize: fontSize[16]
} as const;

const gridItemFlexSx = {display: 'flex'} as const;
const cardTitleSx = {mb: 2, color: colors.text.primary, fontSize: fontSize[20]} as const;
const cardDescSx = {lineHeight: 1.6, color: colors.text.primary} as const;
const checklistLeftColSx = {display: 'flex', alignItems: 'flex-start', gap: 2, width: {xs: '100%', md: '35%'}} as const;
const mobileOnlySx = {display: {xs: 'block', md: 'none'}} as const;
const mobileOnlyFullSx = {display: {xs: 'block', md: 'none'}, width: '100%'} as const;
const mobileCollapseBodySx = {mt: 2, mb: 3, pl: {xs: 5.25, md: 0}} as const;
const mobileListSx = {mt: 2, mb: 2, display: 'flex', flexDirection: 'column', gap: 2.5} as const;
const desktopListSx = {mt: 1, mb: 1, display: 'flex', flexDirection: 'column', gap: 1.5} as const;
const listItemRowSx = {display: 'flex', alignItems: 'center', gap: 2} as const;
const listBulletSx = {width: 10, height: 10, minWidth: 10, bgcolor: colors.primary.deep} as const;
const desktopContentBoxSx = {...checklistContentBoxSx, display: {xs: 'none', md: 'flex'}} as const;
// ─────────────────────────────────────────────────────────────────────────────

// Shared by the mobile Collapse body and the desktop body — only list spacing/layout
// and the list-item className vary between the two (matching pre-existing markup).
function ChecklistBody({item, listSx, listItemClassName}: {item: any; listSx: SxProps<Theme>; listItemClassName?: string}) {
    return (
        <>
            <Typography className="remove-underline" style={{whiteSpace: 'pre-line'}}>
                {item.description ? parse(item.description) : ''}
            </Typography>

            {item.isList && (
                <Box sx={listSx}>
                    {item.listItems?.map((li: any, i: number) => (
                        <Box key={i} sx={listItemRowSx}>
                            <Box sx={listBulletSx} />
                            <Typography sx={listItemTextSx} className={listItemClassName}>
                                {li ? parse(li) : ''}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            )}

            {item.footer && (
                <Typography className="font-headers" sx={footerTextSx}>
                    {item.footer ? parse(item.footer) : ''}
                </Typography>
            )}
        </>
    );
}

const WhyChooseUs = ({heading, subheading, eyebrow, topCardsData: customTopCardsData, checklistData: customChecklistData}: any) => {
    const [activeAccordion, setActiveAccordion] = useState<number | null>(0);

    const cardsToUse = customTopCardsData || topCardsData;
    const checklistToUse = customChecklistData || checklistData;

    return (
        <Box>
            <MUIContainer>
                <SectionHeader
                    eyebrow={eyebrow}
                    heading={heading}
                    subheading={subheading}
                    subheadingClassName="generative-transforming-subheading"
                    subheadingSx={{mb: {xs: 2, md: 6}}}
                />
                <Grid container spacing={4}>
                    {cardsToUse?.map((card: any, idx: number) => (
                        <Grid item xs={12} md={4} key={idx} sx={gridItemFlexSx}>
                            <AnimatedComponent variants={fadeInUp} delay={idx * 0.1} style={{height: '100%', width: '100%'}}>
                                <Box sx={topCardSx}>
                                    <Box sx={topCardImageWrapperSx}>
                                        {card.image && (
                                            <DisplayImage src={card.image} alt={card.title} fill style={{objectFit: 'contain'}} />
                                        )}
                                    </Box>
                                    <Typography variant="h5" component="h3" sx={cardTitleSx} className="font-headers">
                                        {card.title}
                                    </Typography>
                                    <Typography sx={cardDescSx} className="font-text-regular remove-underline">
                                        {parse(card.description)}
                                    </Typography>
                                </Box>
                            </AnimatedComponent>
                        </Grid>
                    ))}
                </Grid>

                {/* Bottom Checklist */}
                {checklistToUse?.length > 0 && (
                    <Box sx={checklistContainerSx}>
                        {checklistToUse?.map((item: any, idx: number) => {
                            const isActive = activeAccordion === idx;

                            return (
                                <Box key={idx} sx={checklistItemSx}>
                                    <Box
                                        sx={checklistLeftColSx}
                                        onClick={() => {
                                            if (globalThis.innerWidth < 900) {
                                                setActiveAccordion(isActive ? null : idx);
                                            }
                                        }}
                                    >
                                        <Box sx={checkIconWrapperSx}>
                                            <CheckCircleOutlineIcon sx={{color: colors.text.primary, fontSize: fontSize[22]}} />
                                        </Box>

                                        <Typography component="h3" className="font-headers" sx={checklistTitleSx}>
                                            {item.title ? parse(item.title) : ''}
                                        </Typography>
                                        <Box sx={mobileOnlySx}>{isActive ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</Box>
                                    </Box>

                                    <Box sx={mobileOnlyFullSx}>
                                        <Collapse in={isActive} timeout="auto" unmountOnExit>
                                            <Box sx={mobileCollapseBodySx}>
                                                <ChecklistBody item={item} listSx={mobileListSx} />
                                            </Box>
                                        </Collapse>
                                    </Box>

                                    {/* Desktop Content */}
                                    <Box sx={desktopContentBoxSx}>
                                        <ChecklistBody item={item} listSx={desktopListSx} listItemClassName="remove-underline" />
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </MUIContainer>
        </Box>
    );
};

export default memo(WhyChooseUs);
